import React, { useState, useEffect } from 'react';
import { useDataTable } from '../hooks/useDataTable';
import DataTable from '../components/shared/DataTable';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';

const ACTION_LABELS = {
  CREATE: { label: 'Oluşturma', icon: 'fa-plus-circle', color: '#28a745' },
  UPDATE: { label: 'Güncelleme', icon: 'fa-edit', color: '#17a2b8' },
  DELETE: { label: 'Silme', icon: 'fa-trash', color: '#dc3545' },
  CANCEL: { label: 'İptal', icon: 'fa-times-circle', color: '#e67e22' },
  UPGRADE: { label: 'Yükseltme', icon: 'fa-arrow-up', color: '#6f42c1' },
  ADDRESS_CHANGE: { label: 'Adres Değişikliği', icon: 'fa-map-marker-alt', color: '#20c997' }
};

const ENTITY_LABELS = {
  SUBSCRIPTION: { label: 'Abonelik', icon: 'fa-sync' },
  ORDER: { label: 'Sipariş', icon: 'fa-shopping-cart' },
  PRODUCT: { label: 'Ürün', icon: 'fa-box' },
  USER: { label: 'Kullanıcı', icon: 'fa-user' }
};

function AuditLogsPage() {
  const [expandedRow, setExpandedRow] = useState(null);

  const {
    data: auditLogs,
    loading,
    pagination,
    filters,
    handlePageChange,
    handleLimitChange,
    handleFilterChange,
    refresh
  } = useDataTable('/admin/audit-logs', {
    limit: 20,
    initialSort: { field: 'createdAt', order: 'desc' },
    initialFilters: {
      entityType: '',
      action: '',
      startDate: '',
      endDate: ''
    }
  });

  const [localSearchTerm, setLocalSearchTerm] = useState(filters.search || '');
  const [localEntityId, setLocalEntityId] = useState(filters.entityId || '');

  // Keep local states in sync if filters are cleared externally (e.g. by clearFilters)
  useEffect(() => {
    setLocalSearchTerm(filters.search || '');
    setLocalEntityId(filters.entityId || '');
  }, [filters.search, filters.entityId]);

  const handleSearchSubmit = () => {
    handleFilterChange('search', localSearchTerm);
    handleFilterChange('entityId', localEntityId);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderJsonDiff = (previous, current) => {
    if (!previous && !current) return <span className="text-muted">Veri yok</span>;

    return (
      <div className="row g-2">
        {previous && (
          <div className="col-md-6">
            <div className="p-2 rounded" style={{ backgroundColor: '#fff5f5', border: '1px solid #fed7d7' }}>
              <small className="fw-bold text-danger d-block mb-1">
                <i className="fas fa-minus-circle me-1"></i>Önceki
              </small>
              <pre className="mb-0" style={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '200px', overflow: 'auto' }}>
                {JSON.stringify(previous, null, 2)}
              </pre>
            </div>
          </div>
        )}
        {current && (
          <div className="col-md-6">
            <div className="p-2 rounded" style={{ backgroundColor: '#f0fff4', border: '1px solid #c6f6d5' }}>
              <small className="fw-bold text-success d-block mb-1">
                <i className="fas fa-plus-circle me-1"></i>Sonraki
              </small>
              <pre className="mb-0" style={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '200px', overflow: 'auto' }}>
                {JSON.stringify(current, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    );
  };

  const columns = [
    {
      key: 'createdAt',
      label: 'Tarih',
      render: (row) => (
        <div>
          <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>
            {formatDate(row.createdAt)}
          </div>
          <small className="text-muted">
            {formatDistanceToNow(new Date(row.createdAt), { addSuffix: true, locale: tr })}
          </small>
        </div>
      )
    },
    {
      key: 'entityType',
      label: 'Kaynak',
      render: (row) => {
        const entity = ENTITY_LABELS[row.entityType] || { label: row.entityType, icon: 'fa-circle' };
        return (
          <div className="d-flex align-items-center gap-2">
            <div className="rounded-circle d-flex align-items-center justify-content-center"
              style={{ width: 28, height: 28, backgroundColor: 'rgba(13,110,253,0.08)' }}>
              <i className={`fas ${entity.icon}`} style={{ fontSize: '0.7rem', color: '#0d6efd' }}></i>
            </div>
            <div>
              <div style={{ fontSize: '0.85rem' }}>{entity.label}</div>
              <small className="text-muted">#{row.entityId}</small>
            </div>
          </div>
        );
      }
    },
    {
      key: 'action',
      label: 'İşlem',
      render: (row) => {
        const act = ACTION_LABELS[row.action] || { label: row.action, icon: 'fa-circle', color: '#6c757d' };
        return (
          <span className="badge d-inline-flex align-items-center gap-1 px-2 py-1"
            style={{
              backgroundColor: `${act.color}15`,
              color: act.color,
              border: `1px solid ${act.color}30`,
              fontSize: '0.78rem',
              fontWeight: 500
            }}>
            <i className={`fas ${act.icon}`} style={{ fontSize: '0.65rem' }}></i>
            {act.label}
          </span>
        );
      }
    },
    {
      key: 'description',
      label: 'Açıklama',
      render: (row) => (
        <span style={{ fontSize: '0.85rem' }}>{row.description || <span className="text-muted">-</span>}</span>
      )
    },
    {
      key: 'targetUser',
      label: 'Müşteri (Hedef)',
      render: (row) => {
        if (!row.targetSnapshot) {
          return <span className="text-muted" style={{ fontSize: '0.85rem' }}>-</span>;
        }
        return (
          <div>
            <div style={{ fontSize: '0.85rem' }}>{row.targetSnapshot.fullName || '-'}</div>
            <small className="text-muted">{row.targetSnapshot.email || `#${row.targetSnapshot.id}`}</small>
          </div>
        );
      }
    },
    {
      key: 'performer',
      label: 'Yapan Kişi',
      render: (row) => {
        if (!row.actorSnapshot) {
          return <span className="text-muted" style={{ fontSize: '0.85rem' }}>Sistem</span>;
        }
        return (
          <div>
            <div style={{ fontSize: '0.85rem' }}>{row.actorSnapshot.fullName || '-'}</div>
            <small className="text-muted">{row.actorSnapshot.role || `#${row.actorSnapshot.id}`}</small>
          </div>
        );
      }
    },
    {
      key: 'details',
      label: '',
      render: (row) => (
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={(e) => { e.stopPropagation(); toggleRow(row.id); }}
          title="Detayları göster"
          style={{ fontSize: '0.75rem' }}
        >
          <i className={`fas fa-chevron-${expandedRow === row.id ? 'up' : 'down'}`}></i>
        </button>
      )
    }
  ];

  const clearFilters = () => {
    handleFilterChange('entityType', '');
    handleFilterChange('action', '');
    handleFilterChange('entityId', '');
    handleFilterChange('search', '');
    handleFilterChange('startDate', '');
    handleFilterChange('endDate', '');
  };

  const hasActiveFilters = filters.entityType || filters.action || filters.entityId || filters.search || filters.startDate || filters.endDate;

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1 fw-bold">
            <i className="fas fa-history me-2 text-primary"></i>
            Denetim Kayıtları
          </h4>
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
            Sistem üzerinde yapılan kritik değişikliklerin geçmişi
          </p>
        </div>
        <button className="btn btn-outline-secondary btn-sm" onClick={refresh}>
          <i className="fas fa-refresh me-1"></i> Yenile
        </button>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body py-3">
          <div className="row g-2 align-items-end">
            <div className="col-md-2 col-6">
              <label className="form-label small text-muted mb-1">Kaynak Türü</label>
              <select
                className="form-select form-select-sm"
                value={filters.entityType || ''}
                onChange={(e) => handleFilterChange('entityType', e.target.value)}
              >
                <option value="">Tümü</option>
                <option value="SUBSCRIPTION">Abonelik</option>
                <option value="ORDER">Sipariş</option>
                <option value="PRODUCT">Ürün</option>
                <option value="USER">Kullanıcı</option>
              </select>
            </div>
            <div className="col-md-2 col-6">
              <label className="form-label small text-muted mb-1">İşlem Türü</label>
              <select
                className="form-select form-select-sm"
                value={filters.action || ''}
                onChange={(e) => handleFilterChange('action', e.target.value)}
              >
                <option value="">Tümü</option>
                <option value="CREATE">Oluşturma</option>
                <option value="UPDATE">Güncelleme</option>
                <option value="CANCEL">İptal</option>
                <option value="UPGRADE">Yükseltme</option>
                <option value="ADDRESS_CHANGE">Adres Değişikliği</option>
                <option value="DELETE">Silme</option>
              </select>
            </div>
            <div className="col-md-2 col-6">
              <label className="form-label small text-muted mb-1">Kaynak ID (Örn: Abone No)</label>
              <input
                type="number"
                placeholder="Örn: 124"
                className="form-control form-control-sm"
                value={localEntityId}
                onChange={(e) => setLocalEntityId(e.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
            </div>
            <div className="col-md-2 col-6">
              <label className="form-label small text-muted mb-1">Kişi Ara (Ad / E-posta)</label>
              <input
                type="text"
                placeholder="Müşteri veya Admin..."
                className="form-control form-control-sm"
                value={localSearchTerm}
                onChange={(e) => setLocalSearchTerm(e.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
            </div>
            <div className="col-md-2 col-6">
              <label className="form-label small text-muted mb-1">Başlangıç</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filters.startDate || ''}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>
            <div className="col-md-2 col-6">
              <label className="form-label small text-muted mb-1">Bitiş</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filters.endDate || ''}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>
            <div className="col-md-2 col-12 d-flex gap-2 align-items-end">
              <button className="btn btn-sm btn-primary w-100" onClick={handleSearchSubmit}>
                <i className="fas fa-search me-1"></i> Filtrele
              </button>
              {hasActiveFilters && (
                <button className="btn btn-sm btn-outline-danger w-100" onClick={clearFilters}>
                  <i className="fas fa-times me-1"></i> Temizle
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="alert alert-light border d-flex align-items-center mb-4 py-2" style={{ fontSize: '0.8rem' }}>
        <i className="fas fa-shield-alt text-primary me-2"></i>
        <span>
          Denetim kayıtları <strong>değiştirilemez ve silinemez</strong>. Tüm kritik işlemler otomatik olarak kaydedilmektedir.
        </span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="text-muted mt-2">Kayıtlar yükleniyor...</p>
        </div>
      ) : auditLogs.length === 0 ? (
        <div className="text-center py-5">
          <i className="fas fa-inbox text-muted mb-3" style={{ fontSize: '2rem' }}></i>
          <p className="text-muted">Henüz denetim kaydı bulunmuyor.</p>
        </div>
      ) : (
        <>
          <div className="card border-0 shadow-sm">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    {columns.map((col) => (
                      <th key={col.key} className="px-3 py-2" style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6c757d' }}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log) => (
                    <React.Fragment key={log.id}>
                      <tr
                        onClick={() => toggleRow(log.id)}
                        style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                        className={expandedRow === log.id ? 'table-active' : ''}
                      >
                        {columns.map((col) => (
                          <td key={col.key} className="px-3 py-2">
                            {col.render(log)}
                          </td>
                        ))}
                      </tr>
                      {expandedRow === log.id && (
                        <tr>
                          <td colSpan={columns.length} className="p-3 bg-light">
                            <div className="ps-2">
                              <h6 className="mb-3 fw-semibold" style={{ fontSize: '0.85rem' }}>
                                <i className="fas fa-exchange-alt me-2 text-primary"></i>
                                Değişiklik Detayı
                              </h6>
                              {renderJsonDiff(log.previousData, log.newData)}
                              {log.metadata && Object.keys(log.metadata).length > 0 && (
                                <div className="mt-2 pt-2 border-top">
                                  <small className="text-muted">
                                    <i className="fas fa-info-circle me-1"></i>
                                    {log.metadata.ip && <span className="me-3">IP: {log.metadata.ip}</span>}
                                    {log.metadata.upgradeMethod && <span className="me-3">Yöntem: {log.metadata.upgradeMethod}</span>}
                                    {log.metadata.cancellationType && <span>İptal Tipi: {log.metadata.cancellationType}</span>}
                                  </small>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mt-3 px-1">
            <div className="d-flex align-items-center gap-2">
              <small className="text-muted">Görünüm:</small>
              <select
                className="form-select form-select-sm"
                value={pagination.limit}
                onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                style={{ width: '70px' }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <small className="text-muted">| Toplam {pagination.total} kayıt | Sayfa {pagination.page} / {pagination.totalPages}</small>
            </div>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${pagination.page <= 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => handlePageChange(pagination.page - 1)}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                </li>
                {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }
                  return (
                    <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(pageNum)}>
                        {pageNum}
                      </button>
                    </li>
                  );
                })}
                <li className={`page-item ${pagination.page >= pagination.totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => handlePageChange(pagination.page + 1)}>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </>
      )}

      <style>{`
        .table > :not(caption) > * > * {
          border-bottom-width: 1px;
          border-color: #f1f3f5;
        }
        .table > thead > tr > th {
          border-bottom: 2px solid #e9ecef;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .table > tbody > tr:hover {
          background-color: rgba(13, 110, 253, 0.03) !important;
        }
        .table > tbody > tr.table-active {
          background-color: rgba(13, 110, 253, 0.05) !important;
        }
        pre {
          font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace;
        }
      `}</style>
    </div>
  );
}

export default AuditLogsPage;
